
import React from "react";
import { Project, Activity } from "@/types";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Clock, CheckCircle, AlertTriangle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";

interface StageActivitiesSummaryProps {
  project: Project;
  onActivityClick?: (stageId: number, activityId: string) => void;
}

export default function StageActivitiesSummary({ project, onActivityClick }: StageActivitiesSummaryProps) {
  // Group activities by stage
  const stageActivities = project.stages.map(stage => {
    const activities = stage.activities || [];
    const completed = activities.filter(a => a.status === "completed").length;
    const inProgress = activities.filter(a => a.status === "in-progress").length;
    const notStarted = activities.filter(a => a.status === "not-started").length;
    const progress = activities.length > 0 
      ? Math.round((completed / activities.length) * 100) 
      : 0;
    
    // Calculate days remaining to next activity due date
    const upcomingActivities = activities
      .filter(a => a.status !== "completed")
      .sort((a, b) => a.dueDate.getTime() - b.dueDate.getTime());
    
    const daysToNextDue = upcomingActivities.length > 0
      ? Math.ceil((upcomingActivities[0].dueDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
      : null;
    
    return {
      stageId: stage.id,
      stageName: stage.name,
      activities,
      completed,
      inProgress,
      notStarted,
      progress,
      daysToNextDue,
      hasOverdue: upcomingActivities.some(a => a.dueDate < new Date()),
      stageStatus: stage.status
    };
  });

  const handleActivityClick = (stageId: number, activityId: string) => {
    if (onActivityClick) {
      onActivityClick(stageId, activityId);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Activities by Stage</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {stageActivities.map(stage => (
            <div key={stage.stageId} className="border-b pb-4 last:border-b-0 last:pb-0">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-medium">Stage {stage.stageId}: {stage.stageName}</h3>
                <Badge variant={
                  stage.stageStatus === "completed" ? "default" : 
                  stage.stageStatus === "in-progress" ? "secondary" : 
                  "outline"
                }>
                  {stage.stageStatus === "completed" ? "Completed" :
                   stage.stageStatus === "in-progress" ? "In Progress" :
                   "Not Started"}
                </Badge>
              </div>
              
              <div className="flex items-center mb-2">
                <div className="w-full">
                  <div className="flex justify-between text-xs mb-1">
                    <span>{stage.progress}% Complete</span>
                    <span>{stage.completed}/{stage.activities.length} Activities</span>
                  </div>
                  <Progress value={stage.progress} />
                </div>
              </div>
              
              <div className="flex items-center justify-between text-sm mt-2">
                <div className="flex items-center">
                  <AlertTriangle className={`h-4 w-4 mr-1 ${stage.hasOverdue ? "text-red-500" : "text-gray-500"}`} />
                  <span className={stage.hasOverdue ? "text-red-500 font-medium" : "text-gray-500"}>
                    {stage.hasOverdue ? "Overdue activities" : "No overdue activities"}
                  </span>
                </div>
                
                {stage.daysToNextDue !== null && (
                  <div className="flex items-center">
                    <Clock className="h-4 w-4 mr-1 text-blue-500" />
                    <span className="text-blue-500">
                      {stage.daysToNextDue > 0
                        ? `${stage.daysToNextDue} days to next due date`
                        : "Due today"
                      }
                    </span>
                  </div>
                )}
              </div>
              
              {stage.activities.length > 0 && stage.stageStatus !== "not-started" && (
                <div className="mt-3">
                  <div className="text-sm font-medium mb-1">Recent Activities</div>
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {stage.activities.slice(0, 3).map(activity => (
                      <div 
                        key={activity.id}
                        className="flex items-center py-1 px-2 rounded-md hover:bg-muted cursor-pointer"
                        onClick={() => handleActivityClick(stage.stageId, activity.id)}
                      >
                        {activity.status === "completed" ? (
                          <CheckCircle className="h-4 w-4 mr-2 text-green-500" />
                        ) : activity.status === "in-progress" ? (
                          <Clock className="h-4 w-4 mr-2 text-blue-500" />
                        ) : (
                          <div className="h-4 w-4 mr-2 rounded-full border border-gray-300" />
                        )}
                        <span className="text-xs truncate">{activity.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
